import { Autocomplete, Button, Grid, Stack, TextField, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { HealthcareServiceWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/HealthcareService';
import { OrganizationWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { WrappedServiceRequest } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import {
  ValueSetWrapper,
  WrappedValueSet,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import {
  useHealthcareServices,
  useOrganization,
  useValueSets,
} from 'src/careflow/ph-core/fhir-client/fhir-react';
import CustomModal, {
  CustomModalBasicProps,
  GridItem,
  GridSection,
} from 'src/components/CustomModal';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useObjectState from 'src/hooks/useObjectState';
import { ValueSetComposeIncludeConcept } from 'src/careflow/ph-core/fhir-client/resources';
import { IconWrapperStyle } from 'src/sections/@dashboard/general/e-commerce/EcommerceWidgetSummary';
import { ActionData } from 'src/careflow/care-coordination/case/goal/grid/GoalModal';
import SearchMember, {
  MemberResourceTypeOption,
  SearchMemberProps,
} from 'src/careflow/care-coordination/case/detail/components/SearchMember/SearchMember';
import {
  TaskSelectedMemberOption,
  TaskSelectedMemberValue,
  getOwnerDisplayLabel,
} from 'src/careflow/care-coordination/task/patient/TasksGrid/TaskModal';
import { getValueSetConceptValue } from 'src/careflow/helpers/common';
import { getFhirIdFromEntity, getWrappedByReference } from 'src/utils/fhir';
import { isValidJsonString } from 'src/utils/string';
import ReferralAutocomplete, {
  ReferralAutocompleteOption,
  ReferralOptionCode,
} from '../ReferralAutocomplete';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';

export type ReferralReferedOption = MemberResourceTypeOption;

interface ReferralInformationFormState {
  referred: TaskSelectedMemberValue | null;
  cause: ValueSetComposeIncludeConcept | null;
  referredDetails: string | null;
  causeDetails: string | null;
  externalOrgType: ValueSetComposeIncludeConcept | null;
  externalOrgName: string | null;
}

interface ReferralSelectedMemberState {
  isSearchMemberOpen: boolean;
  selectedReferred: TaskSelectedMemberOption | TaskSelectedMemberValue | null;
  referredOptions: (TaskSelectedMemberOption | TaskSelectedMemberValue)[];
}
export interface ErrorFieldState {
  referralCause?: string | null;
  referralReferred?: string | null;
  externalOrgType?: string | null;
}

interface Props extends CustomModalBasicProps {
  patient: WrappedPatient | null;
  serviceRequest: WrappedServiceRequest | null;
  open: boolean;
  isLoading: boolean;
  handlerSave: (data: any) => Promise<any>;
  relatedPersonOpen: boolean;
  setRelatedPersonOpen: Dispatch<SetStateAction<boolean>>;
}

export default function ReferralInformation({
  patient,
  serviceRequest,
  open,
  isLoading,
  onClose,
  handlerSave,
  relatedPersonOpen,
  setRelatedPersonOpen,
}: Props) {
  const { i18n } = useLocales();
  const currentUser = useAuth().getCurrentUser();
  const { componentsData } = useTenantConfigData();
  const { externalOrganizationCode } = componentsData?.workflow?.referralInformation ?? {};

  const title = [
    `${i18n('patientEngagement.details.title', 'engagement')}`,
    `${i18n('patientEngagement.details.workflow.step.assignCoordinator', 'engagement')}`,
    `${i18n(
      'patientEngagement.details.workflow.checklist.referralInformation.title',
      'engagement'
    )}`,
  ];

  const [organization, { isFetching: isOrganizationFetching }] = useOrganization(
    getFhirIdFromEntity(currentUser?.organization_fhir_uri),
    {
      autofetch: currentUser?.organization_fhir_uri ? true : false,
      map: OrganizationWrapper,
    }
  );

  const [healthCareService, { isFetching: isLoadingHealthcareServide }] = useHealthcareServices({
    filter: { 'service-type': 'enroll-to-sbha-program' },
    map: HealthcareServiceWrapper,
  });

  const valueSetsIds = [
    'ph-service-request-category',
    'ph-service-request-type',
    'ph-reason-referral',
    'ph-service-request-path-category',
    'ph-external-referral-org-type',
  ];

  const [valueSets, { isFetching: isValueSetsFetching }] = useValueSets({
    filter: {
      _id: valueSetsIds.join(),
    },
    map: ValueSetWrapper,
  });

  const [serviceRequestCategory, serviceRequestType, reasonReferral, serviceRequestPathCategory, externalReferralOrgType] = useMemo(() => {
    if (!valueSets?.length) return [];

    return valueSetsIds.map((valueSetId) => valueSets.find(({ id }) => id === valueSetId) ?? null);
  }, [valueSets]);

  const setServiceRequestType = (serviceRequestType?.asListAll?.() ?? []).find(
    ({ code }) => code === 'enroll-to-sbha-program'
  );

  const setServiceRequestCategory = (serviceRequestCategory?.asListAll?.() ?? []).find(
    ({ code }) => code === 'intake-referral'
  );

  const setServiceRequestPathCatetory = (serviceRequestPathCategory?.asListAll?.() ?? []).find(
    ({ code }) => code === 'inbound-referral'
  );

  const [{ referred, cause, referredDetails, causeDetails, externalOrgType, externalOrgName }, updateState] =
    useObjectState<ReferralInformationFormState>(
      getInitialState({
        serviceRequest: null,
        reasonReferralValues: null,
      })
    );

  const [{ isSearchMemberOpen, selectedReferred, referredOptions }, updateSelectedMember] =
    useObjectState<ReferralSelectedMemberState>(getInitialSelectedState(!!patient));

  const [errors, updateErrorState] = useObjectState<ErrorFieldState>({
    referralReferred: null,
    referralCause: null,
  });

  useEffect(() => {
    if (!open) return;

    const getReferredFromServiceRequest = async () => {
      const referralSourceCode = (serviceRequest?.category as any[])?.find(
        (c: any) =>
          c?.coding?.[0]?.system ===
          'https://mycarecircle.site/fhir/CodeSystem/ph-referral-source-type'
      )?.coding?.[0]?.code;
      if (referralSourceCode === 'external') {
        updateState({ referred: null });
        updateSelectedMember({ selectedReferred: null });
        return;
      }
      const { referred } = await getReferred(serviceRequest);
      updateState({ referred: referred });
      updateSelectedMember({ selectedReferred: referred });
    };

    getReferredFromServiceRequest();

    updateState(
      getInitialState({
        serviceRequest,
        reasonReferralValues: reasonReferral,
      })
    );

    const referralSourceCategory = (serviceRequest?.category as any[])?.find(
      (c: any) => c?.coding?.[0]?.system === 'https://mycarecircle.site/fhir/CodeSystem/ph-referral-source-type'
    );
    setSelectedReferredCodeOption(referralSourceCategory?.coding?.[0]?.code);

    updateErrorState({
      referralReferred: null,
      referralCause: null,
      externalOrgType: null,
    });
    updateSelectedMember(getInitialSelectedState(!!patient));
  }, [open, serviceRequest, patient]);

  useEffect(() => {
    if (!open || cause || !reasonReferral) return;
    const savedCause = getSavedCause(serviceRequest, reasonReferral);
    if (savedCause) updateState({ cause: savedCause });
  }, [reasonReferral]);

  const handleOnSave = async () => {
    const errors: ErrorFieldState = {};

    if (selectedReferredCodeOption === 'external') {
      if (!externalOrgType) errors.externalOrgType = 'Must specify an External Organization Type';
      if (externalOrgType?.code === 'other' && !externalOrgName?.trim())
        errors.externalOrgType = 'Must specify an Organization Name';
      if (!cause)
        errors.referralCause = 'Must specify a Cause';
    } else if (isEmpty(cause) && isEmpty(referred)) {
      if (!referred) errors.referralReferred = 'Must specify a Referred';
      if (!cause) errors.referralCause = 'Must specify a Cause';
    }

    if (Object.keys(errors).length) {
      updateErrorState({ ...errors });
      return;
    }

    updateErrorState({
      referralReferred: null,
      referralCause: null,
      externalOrgType: null,
    });

    const { actionCall, payload: serviceRequestData, successfulMessage } = mapServiceRequest();

    await handlerSave(serviceRequestData);
    onClose?.({}, 'backdropClick');
  };

  const mapServiceRequest = (): ActionData => {
    const externalExtensions: any[] = [];
    if (selectedReferredCodeOption === 'external' && externalOrgType) {
      externalExtensions.push({
        url: 'https://mycarecircle.site/fhir/extension/external-referral-org-type',
        valueCodeableConcept: {
          coding: [
            {
              system: 'https://mycarecircle.site/fhir/CodeSystem/ph-external-referral-org-type',
              code: externalOrgType.code,
              display: externalOrgType.display,
            },
          ],
          text: externalOrgType.display,
        },
      });
      if (externalOrgType.code === 'other' && externalOrgName) {
        externalExtensions.push({
          url: 'https://mycarecircle.site/fhir/extension/external-referral-org-name',
          valueString: externalOrgName.trim(),
        });
      }
      if (referredDetails?.trim()) {
        externalExtensions.push({
          url: 'https://mycarecircle.site/fhir/extension/external-referral-details',
          valueString: referredDetails.trim(),
        });
      }
    }

    const referralSourceLabels: Record<string, string> = {
      internal: 'Internal Referral',
      external: 'External Referral',
      self: 'Self-Referral',
      'parent-guardian': 'Parent / Guardian',
    };

    return {
      payload: {
        id: serviceRequest?.id ?? null,
        ...(selectedReferredCodeOption !== 'external' && referred
          ? {
              referredFrom: {
                reference: `${referred?.resourceType}/${referred?.id}`,
                display: `${referredDetails ? `${referredDetails}` : ''}`,
              },
            }
          : null),
        extension: externalExtensions,
        ...(cause
          ? {
              reasonCode: [
                {
                  coding: [cause],
                  text: `${causeDetails ? `${causeDetails}` : ''}`,
                },
              ],
            }
          : null),
        category: [
          {
            coding: [setServiceRequestCategory],
            text: setServiceRequestCategory?.display,
          },
          {
            coding: [setServiceRequestPathCatetory],
            text: setServiceRequestPathCatetory?.display,
          },
          ...(selectedReferredCodeOption
            ? [
                {
                  coding: [
                    {
                      system: 'https://mycarecircle.site/fhir/CodeSystem/ph-referral-source-type',
                      code: selectedReferredCodeOption,
                      display: referralSourceLabels[selectedReferredCodeOption],
                    },
                  ],
                  text: selectedReferredCodeOption,
                },
              ]
            : []),
        ],
        serviceType: {
          coding: [setServiceRequestType],
          text: setServiceRequestType?.display,
        },
        intent: 'directive',
        status: 'active',
        patientId: patient?.id,
        patientName: patient?.getFullName(),
        serviceId: healthCareService?.[0]?.id
          ? `HealthcareService/${healthCareService?.[0]?.id}`
          : null,
        serviceName: healthCareService?.[0]?.name ?? null,
      },
      actionCall: () => {},
      successfulMessage: '',
    };
  };

  const isAllLoading =
    isLoading || isValueSetsFetching || isOrganizationFetching || isLoadingHealthcareServide;

  const [selectedReferredCodeOption, setSelectedReferredCodeOption] = useState<string>();

  function getReferredOptionLabel() {
    const resource = referred;

    const resolvers: Record<ReferralOptionCode | 'default', () => string | undefined> = {
      default: () => (resource ? getDisplay(resource) : undefined),

      internal: () => (organization ? getDisplay(organization) : undefined),

      external: () =>
        resource?.resourceType === 'Organization' ? getDisplay(resource) : 'External Referral',

      'parent-guardian': () => {
        if (resource?.resourceType !== 'RelatedPerson') return;

        const display = getDisplay(resource);
        const relationship = resource?.relationship?.[0]?.coding?.[0]?.display;

        return relationship ? `${display} (${relationship})` : display;
      },

      self: () => (patient ? getDisplay(patient) : undefined),
    };

    const key: ReferralOptionCode = (selectedReferredCodeOption as ReferralOptionCode) ?? 'default';

    return resolvers[key]();
  }

  const referredOptionLabel = useMemo(getReferredOptionLabel, [
    selectedReferredCodeOption,
    patient,
    organization,
    referred,
  ]);

  function handleReferralAutocompleteOnChangeOption(
    option: ReferralAutocompleteOption | undefined
  ) {
    if (!option) {
      setSelectedReferredCodeOption(undefined);
      updateSelectedMember({ selectedReferred: null });
      updateState({ referred: null });
      return;
    }

    setSelectedReferredCodeOption(option.code);

    if (option.code === 'internal') {
      updateSelectedMember({
        isSearchMemberOpen: false,
        selectedReferred: organization,
      });
      updateState({ referred: organization });
      return;
    } else if (option.code === 'external') {
      updateSelectedMember({
        isSearchMemberOpen: false,
        selectedReferred: null,
      });
      updateState({ referred: null, externalOrgType: null, externalOrgName: null });
      return;
    } else if (option.code === 'self') {
      updateSelectedMember({
        isSearchMemberOpen: false,
        selectedReferred: patient,
      });
      updateState({ referred: patient });
      return;
    } else if (option.code === 'parent-guardian') {
      const rOption: TaskSelectedMemberOption = {
        label: 'Parent / Guardian',
        value: 'RelatedPerson',
      };

      updateSelectedMember({
        isSearchMemberOpen: true,
        selectedReferred: rOption,
      });
      return;
    }

    updateSelectedMember({ selectedReferred: null });
    updateState({ referred: null });
  }

  const handleAddNewParentGuardian = () => {
    setRelatedPersonOpen(true);
  };

  const ParentGuardianHeader = () =>
    // @ts-ignore
    selectedReferred?.value === 'RelatedPerson' ? (
      <Button sx={{ marginLeft: 'auto' }} onClick={handleAddNewParentGuardian}>
        {`${i18n(
          'patientEngagement.details.workflow.checklist.referralInformation.addNew',
          'engagement'
        )}`}
      </Button>
    ) : null;

  return (
    <CustomModal
      keepMounted
      open={open}
      title={title[2]}
      breadcrumbs={title}
      onSave={handleOnSave}
      onCancel={onClose as Function}
      onClose={onClose}
      isLoading={isAllLoading}
      containerSx={[
        { overflow: 'scroll' },
        isSearchMemberOpen ? { width: '94vw' } : { width: '70vw' },
      ]}
    >
      <Grid container my={3} justifyContent={'space-between'}>
        <Grid item xs={isSearchMemberOpen ? 6 : 12}>
          <GridSection mt={0}>
            <Typography variant="caption" sx={{ color: 'gray' }}>
              {`${i18n(
                'patientEngagement.details.workflow.checklist.referralInformation.comment',
                'engagement'
              )}`}
            </Typography>
          </GridSection>
          <GridSection>
            <GridItem>
              <Stack direction="row" alignItems="center">
                <IconWrapperStyle sx={{ bgcolor: 'green', color: 'white' }}>1 </IconWrapperStyle>
                <Typography variant="body2" component="span" noWrap sx={{ color: 'text.primary' }}>
                  &nbsp;
                  {`${i18n(
                    'patientEngagement.details.workflow.checklist.referralInformation.question1',
                    'engagement'
                  )}`}
                </Typography>
              </Stack>
            </GridItem>
            <GridItem>
              <Stack direction="row" alignItems="center">
                <IconWrapperStyle sx={{ bgcolor: 'green', color: 'white' }}>2</IconWrapperStyle>
                <Typography variant="body2" component="span" noWrap sx={{ color: 'text.primary' }}>
                  &nbsp;
                  {`${i18n(
                    'patientEngagement.details.workflow.checklist.referralInformation.question2',
                    'engagement'
                  )}`}
                </Typography>
              </Stack>
            </GridItem>
          </GridSection>

          <GridSection>
            <GridItem>
              <ReferralAutocomplete
                patient={patient}
                errors={errors}
                optionLabel={referredOptionLabel}
                onChangeOption={handleReferralAutocompleteOnChangeOption}
              />
            </GridItem>

            <GridItem>
              <Autocomplete
                disabled={isAllLoading}
                value={cause}
                fullWidth
                onChange={(_: React.SyntheticEvent, cause) => {
                  updateState({ cause });
                }}
                options={reasonReferral?.asListAll?.() ?? []}
                getOptionLabel={({ display }) => display as string}
                isOptionEqualToValue={(opt, val) => opt.code === val.code}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!errors?.referralCause}
                    helperText={errors?.referralCause}
                    label={`${i18n(
                      'patientEngagement.details.workflow.checklist.referralInformation.cause',
                      'engagement'
                    )}*`}
                    placeholder={`Select ${i18n(
                      'patientEngagement.details.workflow.checklist.referralInformation.cause',
                      'engagement'
                    )}...`}
                    variant="outlined"
                  />
                )}
              />
            </GridItem>
          </GridSection>

          {selectedReferredCodeOption === 'external' && (
            <GridSection>
              <GridItem>
                <Autocomplete
                  disabled={isAllLoading}
                  value={externalOrgType}
                  fullWidth
                  onChange={(_: React.SyntheticEvent, value) => {
                    updateState({ externalOrgType: value, externalOrgName: null });
                    updateErrorState({ externalOrgType: null });
                  }}
                  options={externalReferralOrgType?.asListAll?.() ?? []}
                  getOptionLabel={({ display }) => display as string}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!errors?.externalOrgType}
                      helperText={errors?.externalOrgType}
                      label="External Organization Type*"
                      placeholder="Select organization type..."
                      variant="outlined"
                    />
                  )}
                />
              </GridItem>
              {externalOrgType?.code === 'other' && (
                <GridItem>
                  <TextField
                    fullWidth
                    disabled={isAllLoading}
                    value={externalOrgName ?? ''}
                    onChange={(e) => updateState({ externalOrgName: e.target.value })}
                    label="Organization Name*"
                    placeholder="Enter the organization name..."
                    variant="outlined"
                  />
                </GridItem>
              )}
            </GridSection>
          )}

          <GridSection>
            <GridItem>
              <TextField
                fullWidth
                rows={4}
                multiline
                disabled={isAllLoading}
                value={referredDetails ?? ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  updateState({
                    referredDetails: event.target.value,
                  });
                }}
                label={i18n(
                  'patientEngagement.details.workflow.checklist.referralInformation.details',
                  'engagement'
                )}
                placeholder={`Write Some ${i18n(
                  'patientEngagement.details.workflow.checklist.referralInformation.details',
                  'engagement'
                )}...`}
                variant="outlined"
              />
            </GridItem>
            <GridItem>
              <TextField
                fullWidth
                rows={4}
                multiline
                disabled={isAllLoading}
                value={causeDetails ?? ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  updateState({
                    causeDetails: event.target.value,
                  });
                }}
                label={i18n(
                  'patientEngagement.details.workflow.checklist.referralInformation.details',
                  'engagement'
                )}
                placeholder={`Write Some ${i18n(
                  'patientEngagement.details.workflow.checklist.referralInformation.details',
                  'engagement'
                )}...`}
                variant="outlined"
              />
            </GridItem>
          </GridSection>
        </Grid>
        {isSearchMemberOpen && (
          <Grid item xs={5.9}>
            <SearchMember
              patient={patient as WrappedPatient}
              onSelectResource={(resource) => {
                updateSelectedMember({ selectedReferred: resource, isSearchMemberOpen: false });
                updateState({ referred: resource });
              }}
              filterOrganizationCode={externalOrganizationCode}
              onClear={() => {
                updateSelectedMember({ selectedReferred: null, isSearchMemberOpen: false });
                updateState({ referred: null });
              }}
              externalResourceType={(() => {
                const selectedOption = selectedReferred as ReferralReferedOption;
                return selectedOption?.value as SearchMemberProps['externalResourceType'];
              })()}
              headerComponents={<ParentGuardianHeader />}
            />
          </Grid>
        )}
      </Grid>
    </CustomModal>
  );
}

const getMemberResourceTypes = (showPatient: boolean = true): MemberResourceTypeOption[] => [
  { label: 'Internal Referral', value: 'Organization' },
  { label: 'External Referral', value: 'Organization' },
  { label: 'Parent / Guardian', value: 'RelatedPerson' },
  ...(showPatient
    ? [{ label: 'Self-Referral', value: 'Patient' } as MemberResourceTypeOption]
    : []),
];

export const getReferred = async (serviceRequest: WrappedServiceRequest | null) => {
  const getReferred: ReferralInformationFormState['referred'] = await getWrappedByReference(
    serviceRequest?.requester
  );
  const referred = getReferred;
  const referredDetails = serviceRequest?.requester?.display ?? null;

  return { referred, referredDetails };
};

const getSavedCause = (
  serviceRequest: WrappedServiceRequest | null,
  reasonReferralValues: WrappedValueSet | null
): ValueSetComposeIncludeConcept | null =>
  getValueSetConceptValue(
    reasonReferralValues?.asListAll() ?? [],
    serviceRequest?.reasonCode?.[0]?.coding?.[0]?.code,
    true
  );

const getInitialState = ({
  serviceRequest,
  reasonReferralValues,
}: {
  serviceRequest: WrappedServiceRequest | null;
  reasonReferralValues: WrappedValueSet | null;
}): ReferralInformationFormState => {
  const exts = (serviceRequest?.extension as any[]) ?? [];

  const referralSourceCode = (serviceRequest?.category as any[])?.find(
    (c: any) =>
      c?.coding?.[0]?.system === 'https://mycarecircle.site/fhir/CodeSystem/ph-referral-source-type'
  )?.coding?.[0]?.code;
  const isExternal = referralSourceCode === 'external';

  const externalOrgTypeExt = isExternal
    ? exts.find(
        (e) => e.url === 'https://mycarecircle.site/fhir/extension/external-referral-org-type'
      )
    : null;
  const externalOrgType = externalOrgTypeExt
    ? {
        code: externalOrgTypeExt.valueCodeableConcept?.coding?.[0]?.code ?? '',
        display: externalOrgTypeExt.valueCodeableConcept?.coding?.[0]?.display ?? '',
      }
    : null;

  const externalOrgNameExt = isExternal
    ? exts.find(
        (e) => e.url === 'https://mycarecircle.site/fhir/extension/external-referral-org-name'
      )
    : null;
  const externalOrgName = externalOrgNameExt?.valueString ?? null;

  const externalDetailsExt = isExternal
    ? exts.find(
        (e) => e.url === 'https://mycarecircle.site/fhir/extension/external-referral-details'
      )
    : null;
  const referredDetails =
    externalDetailsExt?.valueString ?? serviceRequest?.requester?.display ?? null;

  const cause = getSavedCause(serviceRequest, reasonReferralValues);
  const causeDetails = serviceRequest?.reasonCode?.[0]?.text ?? null;

  return {
    referred: null,
    cause,
    referredDetails,
    causeDetails,
    externalOrgType: externalOrgType as any,
    externalOrgName,
  };
};

const getInitialSelectedState = (showPatient: boolean) => {
  return {
    isSearchMemberOpen: false,
    selectedReferred: null,
    referredOptions: [...getMemberResourceTypes(showPatient)],
  };
};

export const getDisplay = (option: TaskSelectedMemberValue): string => {
  const ownerDisplayLabel = getOwnerDisplayLabel(option);
  const validDisplay = isValidJsonString(ownerDisplayLabel);
  const display = validDisplay ? JSON.parse(ownerDisplayLabel)?.display : ownerDisplayLabel;
  return display;
};
